// ReminderBox.tsx
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTimes } from '@fortawesome/free-solid-svg-icons';
import './ReminderBox.css';
import { useTranslation } from 'react-i18next';

interface ReminderBoxProps {
  content: React.ReactNode;
  vibrateDelay?: number;  // Delay before starting vibration
  duration?: number;      // How long to show the reminder (optional)
  onClose?: () => void;   // Optional callback when reminder closes
}

const ReminderBox: React.FC<ReminderBoxProps> = ({ 
  content,
  vibrateDelay = 2000,
  duration,
  onClose
}) => {
  const { t } = useTranslation(['resus']);
  const [isVibrating, setIsVibrating] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const vibrateTimer = setTimeout(() => {
      setIsVibrating(true);
      setTimeout(() => setIsVibrating(false), 1500);
    }, vibrateDelay);

    let closeTimer: NodeJS.Timeout | undefined;
    if (duration) {
      closeTimer = setTimeout(() => {
        handleClose();
      }, duration);
    }

    return () => {
      clearTimeout(vibrateTimer);
      if (closeTimer) {
        clearTimeout(closeTimer);
      }
    };
  }, [vibrateDelay, duration]);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.(); // Call onClose callback if provided
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`reminder-box ${isVibrating ? 'vibrate' : ''}`}>
      <div className="reminder-header">
        <h3><FontAwesomeIcon icon={faBell} /> {t('reminderBox.title')}</h3>
        <button 
          data-testid="reminder-box-close-button"
          onClick={handleClose}><FontAwesomeIcon icon={faTimes} /></button>
      </div>
      <div className="reminder-content">
        {content}
      </div>
    </div>
  );
};

export default ReminderBox;
